/**
 * Synthesised sounds (Web Audio oscillators and filtered noise, no files). They are the fallback
 * for the sample set (samples.ts, docs/architecture.md §10): each plays while its sample is not
 * loaded yet, or when it can't load (no AAC decoder, offline).
 * Every sound is fire-and-forget, except the signature charge, which returns a handle.
 */

export interface SoundHandle {
  stop(): void;
}

interface ToneOptions {
  type?: OscillatorType;
  freq: number;
  /** Glide to this frequency over the sound. */
  to?: number;
  /** Delay in seconds. */
  at?: number;
  dur: number;
  gain: number;
  attack?: number;
  pan?: number;
}

interface NoiseOptions {
  filter: BiquadFilterType;
  freq: number;
  to?: number;
  q?: number;
  at?: number;
  dur: number;
  gain: number;
  attack?: number;
  pan?: number;
}

/** Floor for exponential ramps, which can't reach 0. */
const SILENT = 0.0001;
const NOISE_SECONDS = 2;

/** Inharmonic partials of a boxing bell: [ratio, gain, decay seconds]. */
const BELL_PARTIALS = [
  [1, 1, 1.7],
  [2.0, 0.4, 1.1],
  [2.76, 0.45, 0.85],
  [3.9, 0.2, 0.55],
  [5.4, 0.15, 0.35],
] as const;

export class Synth {
  private readonly ctx: AudioContext;
  private readonly out: AudioNode;
  private readonly noiseBuffer: AudioBuffer;

  constructor(ctx: AudioContext, out: AudioNode) {
    this.ctx = ctx;
    this.out = out;
    const length = Math.floor(ctx.sampleRate * NOISE_SECONDS);
    this.noiseBuffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = this.noiseBuffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  }

  // -------------------------------------------------------------------------------------------
  // Building blocks

  private destination(pan = 0): AudioNode {
    if (!pan) return this.out;
    const panner = this.ctx.createStereoPanner();
    panner.pan.value = Math.max(-1, Math.min(1, pan));
    panner.connect(this.out);
    return panner;
  }

  private envelope(param: AudioParam, t: number, attack: number, peak: number, dur: number): void {
    param.setValueAtTime(SILENT, t);
    param.exponentialRampToValueAtTime(Math.max(SILENT, peak), t + attack);
    param.exponentialRampToValueAtTime(SILENT, t + Math.max(dur, attack + 0.01));
  }

  private tone(o: ToneOptions): void {
    const t = this.ctx.currentTime + (o.at ?? 0);
    const osc = this.ctx.createOscillator();
    osc.type = o.type ?? 'sine';
    osc.frequency.setValueAtTime(o.freq, t);
    if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t + o.dur);
    const g = this.ctx.createGain();
    this.envelope(g.gain, t, o.attack ?? 0.004, o.gain, o.dur);
    osc.connect(g).connect(this.destination(o.pan));
    osc.start(t);
    osc.stop(t + o.dur + 0.03);
  }

  private noise(o: NoiseOptions): void {
    const t = this.ctx.currentTime + (o.at ?? 0);
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = o.filter;
    filter.Q.value = o.q ?? 0.8;
    filter.frequency.setValueAtTime(o.freq, t);
    if (o.to) filter.frequency.exponentialRampToValueAtTime(o.to, t + o.dur);
    const g = this.ctx.createGain();
    this.envelope(g.gain, t, o.attack ?? 0.003, o.gain, o.dur);
    src.connect(filter).connect(g).connect(this.destination(o.pan));
    const length = o.dur + 0.05;
    const offset = Math.random() * Math.max(0, NOISE_SECONDS - length);
    src.start(t, offset, length);
  }

  // -------------------------------------------------------------------------------------------
  // Fight sounds

  /** A punch or a dash cutting the air. */
  whoosh(pan: number, heavy = false): void {
    this.noise({
      filter: 'bandpass',
      freq: heavy ? 320 : 700,
      to: heavy ? 2200 : 3200,
      q: heavy ? 0.9 : 1.4,
      dur: heavy ? 0.3 : 0.15,
      gain: heavy ? 0.34 : 0.22,
      attack: heavy ? 0.09 : 0.045,
      pan,
    });
  }

  /** A clean strike landing. */
  thud(pan: number, weight = 1): void {
    this.tone({ freq: 175, to: 58, dur: 0.2, gain: 0.75 * weight, pan });
    this.noise({ filter: 'lowpass', freq: 2600, to: 500, dur: 0.07, gain: 0.42 * weight, pan });
  }

  /** The signature landing. */
  boom(pan: number): void {
    this.tone({ freq: 125, to: 32, dur: 0.65, gain: 1, pan });
    this.tone({ type: 'triangle', freq: 260, to: 60, dur: 0.28, gain: 0.32, pan });
    this.noise({ filter: 'lowpass', freq: 1800, to: 180, dur: 0.5, gain: 0.6, pan });
    this.noise({ filter: 'highpass', freq: 3000, dur: 0.08, gain: 0.18, pan });
  }

  /** A blocked hit. */
  clack(pan: number): void {
    this.noise({ filter: 'bandpass', freq: 2300, q: 3, dur: 0.07, gain: 0.5, pan });
    this.tone({ type: 'triangle', freq: 340, to: 190, dur: 0.08, gain: 0.3, pan });
  }

  /** A parry (Guard rank 5). */
  ting(pan: number, base = 1568): void {
    this.tone({ freq: base, dur: 0.6, gain: 0.2, attack: 0.002, pan });
    this.tone({ freq: base * 2.01, dur: 0.38, gain: 0.09, attack: 0.002, pan });
    this.tone({ freq: base * 3.03, dur: 0.22, gain: 0.05, attack: 0.002, pan });
    this.noise({ filter: 'highpass', freq: 5000, dur: 0.03, gain: 0.12, pan });
  }

  /** The grab before a takedown. */
  grab(pan: number): void {
    this.noise({ filter: 'lowpass', freq: 1200, to: 380, dur: 0.12, gain: 0.42, pan });
    this.tone({ type: 'triangle', freq: 200, to: 120, dur: 0.1, gain: 0.26, pan });
  }

  /** A takedown slam; harder with a metal rattle when it drives into the fence. */
  slam(pan: number, fence: boolean): void {
    this.tone({ freq: 145, to: 30, dur: 0.62, gain: 1, pan });
    this.noise({ filter: 'lowpass', freq: 950, to: 140, dur: 0.48, gain: 0.62, pan });
    this.noise({ filter: 'highpass', freq: 2600, at: 0.01, dur: 0.2, gain: 0.14, pan });
    if (!fence) return;
    for (const [freq, gain] of [
      [410, 0.07],
      [587, 0.055],
      [823, 0.04],
    ] as const) {
      this.tone({ type: 'square', freq, to: freq * 0.97, dur: 0.45, gain, pan });
    }
    this.noise({ filter: 'bandpass', freq: 1400, q: 6, dur: 0.35, gain: 0.2, pan });
  }

  /** A guard broken by Power rank 10. */
  crack(pan: number): void {
    this.noise({ filter: 'bandpass', freq: 900, q: 2, dur: 0.16, gain: 0.45, pan });
    this.tone({ type: 'square', freq: 300, to: 90, dur: 0.16, gain: 0.08, pan });
  }

  /** Not enough stamina: a short "nuh-uh". */
  buzz(pan: number): void {
    this.tone({ type: 'square', freq: 118, dur: 0.08, gain: 0.06, pan });
    this.tone({ type: 'square', freq: 84, at: 0.085, dur: 0.12, gain: 0.06, pan });
  }

  /** Ring bell: `strikes` hits `gap` seconds apart. */
  bell(strikes: number, gap: number, at = 0, gain = 0.26): void {
    for (let i = 0; i < strikes; i++) {
      const start = at + i * gap;
      for (const [ratio, g, decay] of BELL_PARTIALS) {
        this.tone({ freq: 760 * ratio, at: start, dur: decay, gain: gain * g, attack: 0.002 });
      }
      this.noise({ filter: 'highpass', freq: 3500, at: start, dur: 0.02, gain: 0.1 });
    }
  }

  /** The signature wind-up: a rising, brightening hum that lasts `dur` and holds until stopped. */
  charge(pan: number, dur: number): SoundHandle {
    const { ctx } = this;
    const t = ctx.currentTime;
    const peak = 0.13;
    const g = ctx.createGain();
    g.gain.setValueAtTime(SILENT, t);
    g.gain.linearRampToValueAtTime(peak, t + dur);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.Q.value = 5;
    filter.frequency.setValueAtTime(300, t);
    filter.frequency.exponentialRampToValueAtTime(3400, t + dur);
    filter.connect(g).connect(this.destination(pan));
    const oscs = [0, 14].map((detune) => {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.detune.value = detune;
      osc.frequency.setValueAtTime(110, t);
      osc.frequency.exponentialRampToValueAtTime(640, t + dur);
      osc.connect(filter);
      osc.start(t);
      return osc;
    });
    let stopped = false;
    const stop = () => {
      if (stopped) return;
      stopped = true;
      clearTimeout(safety);
      const now = ctx.currentTime;
      const progress = Math.min(1, Math.max(0, (now - t) / dur));
      g.gain.cancelScheduledValues(now);
      g.gain.setValueAtTime(Math.max(SILENT, peak * progress), now);
      g.gain.exponentialRampToValueAtTime(SILENT, now + 0.07);
      for (const osc of oscs) osc.stop(now + 0.09);
    };
    // Never outlive the wind-up for long, even if nobody stops it.
    const safety = setTimeout(stop, (dur + 1) * 1000);
    return { stop };
  }

  /** A fighter rocked: a wobbling, falling tone. */
  dizzy(pan: number): void {
    const { ctx } = this;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(900, t);
    osc.frequency.exponentialRampToValueAtTime(500, t + 0.9);
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 7;
    const depth = ctx.createGain();
    depth.gain.value = 45;
    lfo.connect(depth).connect(osc.frequency);
    const g = ctx.createGain();
    this.envelope(g.gain, t, 0.02, 0.14, 0.95);
    osc.connect(g).connect(this.destination(pan));
    for (const o of [osc, lfo]) {
      o.start(t);
      o.stop(t + 1);
    }
  }

  /** A read punished: the signature landing with a bright ting on top. */
  punish(pan: number): void {
    this.boom(pan);
    this.ting(pan, 2093);
  }

  // -------------------------------------------------------------------------------------------
  // UI sounds

  /** Level-up or skill point: a quick rising arpeggio. */
  popup(): void {
    [1047, 1319, 1568, 2093].forEach((freq, i) => {
      this.tone({ type: 'triangle', freq, at: i * 0.07, dur: 0.35, gain: 0.12 });
    });
  }

  /** Result stinger: won. */
  win(): void {
    [523, 659, 784, 1047].forEach((freq, i) => {
      this.tone({ type: 'triangle', freq, at: i * 0.11, dur: i === 3 ? 0.7 : 0.3, gain: 0.16 });
    });
  }

  /** Result stinger: lost (a falling "wah-wah"). */
  lose(): void {
    [349, 330, 311, 294].forEach((freq, i) => {
      this.tone({ type: 'triangle', freq, at: i * 0.22, dur: i === 3 ? 0.8 : 0.25, gain: 0.16 });
    });
  }
}
